import React, { useRef, useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useSpring, useVelocity } from "framer-motion";
import "./Home.css";
import TreeVine from "../../components/TreeVine/TreeVine";
import useTreeScroll from "../../components/TreeCanvas/useTreeScroll";
import profilePhoto from "./Images/me.png";

/* ---------- Letter-by-letter name reveal ---------- */
function LetterReveal({ text, className, delay = 0 }) {
  return (
    <span className={className} aria-label={text}>
      {text.split("").map((ch, i) => (
        <span
          key={i}
          className="letter-reveal-char"
          style={{ "--char-index": i, "--char-delay": `${delay + i * 50}ms` }}
        >
          {ch === " " ? "\u00A0" : ch}
        </span>
      ))}
    </span>
  );
}

/* ---------- Branch section definitions ---------- */
// Card Y positions match BRANCH_YS in TreeVine (3 branches).
// Trunk tip reaches Y when progress = (Y - 0.02) / 0.96.
// Y=0.30 → p=0.29, Y=0.50 → p=0.50, Y=0.70 → p=0.71
const SECTIONS = [
  {
    label: "Energy",
    to: "/energy",
    desc: "SCADA, HVPP & market systems",
    icon: "⚡",
    range: [0.27, 0.34, 0.44, 0.49],
    side: "left",
    top: "30%",
  },
  {
    label: "Robotics",
    to: "/robotics",
    desc: "Autonomous systems & hardware",
    icon: "🤖",
    range: [0.48, 0.55, 0.65, 0.70],
    side: "right",
    top: "50%",
  },
  {
    label: "Arctic",
    to: "/arctic",
    desc: "AI chatbots & LLM solutions",
    icon: "❄️",
    range: [0.69, 0.76, 0.86, 0.92],
    side: "left",
    top: "70%",
  },
];

/* ---------- Card animation state machine ---------- */
// Returns: "hidden" | "entering" | "sitting" | "exiting" | "gone"
function getCardPhase(progress, range) {
  const [enterStart, enterEnd, exitStart, exitEnd] = range;
  if (progress < enterStart) return "hidden";
  if (progress < enterEnd) return "entering";
  if (progress < exitStart) return "sitting";
  if (progress < exitEnd) return "exiting";
  return "gone";
}

// Framer motion variants for each phase
const cardVariants = {
  hidden: (side) => ({
    opacity: 0,
    x: side === "left" ? -80 : 80,
    y: "-50%",
    rotateY: side === "left" ? 18 : -18,
    scale: 0.88,
    filter: "blur(8px)",
  }),
  entering: {
    opacity: 1,
    x: 0,
    y: "-50%",
    rotateY: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  },
  sitting: {
    opacity: 1,
    x: 0,
    y: "-50%",
    rotateY: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: { duration: 0.15 },
  },
  exiting: (side) => ({
    opacity: 0,
    x: side === "left" ? -40 : 40,
    y: "-50%",
    rotateY: side === "left" ? -10 : 10,
    scale: 0.92,
    filter: "blur(6px)",
    transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] },
  }),
  gone: (side) => ({
    opacity: 0,
    x: side === "left" ? -40 : 40,
    y: "-50%",
    rotateY: side === "left" ? -10 : 10,
    scale: 0.92,
    filter: "blur(6px)",
  }),
};

/* ---------- Single branch card ---------- */
function BranchCard({ section, progress }) {
  const phase = getCardPhase(progress, section.range);

  // Progress within the entering phase (0→1) for subtle Y float
  const enterProgress = useMemo(() => {
    const [s, e] = section.range;
    if (progress < s || progress > e) return 0;
    return Math.min((progress - s) / (e - s), 1);
  }, [progress, section.range]);

  if (phase === "hidden" || phase === "gone") return null;

  return (
    <motion.div
      className={`home-branch-card ${section.side}`}
      custom={section.side}
      variants={cardVariants}
      initial="hidden"
      animate={phase}
      style={{
        top: `calc(${section.top} + ${Math.sin(enterProgress * Math.PI) * -6}px)`,
      }}
    >
      <Link to={section.to} className="home-branch-card-inner">
        <div className="home-branch-card-icon">{section.icon}</div>
        <div className="home-branch-card-body">
          <span className="home-branch-card-label">{section.label}</span>
          <span className="home-branch-card-desc">{section.desc}</span>
        </div>
        <span className="home-branch-card-arrow">→</span>
      </Link>
    </motion.div>
  );
}

/* ---------- StatCounter ---------- */
function StatCounter({ label, end }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const duration = 1200;
          const startTime = performance.now();
          const animate = (now) => {
            const elapsed = now - startTime;
            const p = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setCount(Math.floor(eased * end));
            if (p < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
          observer.unobserve(el);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [end]);

  return (
    <div className="home-stat" ref={ref}>
      <span className="home-stat-number">{count}+</span>
      <span className="home-stat-label">{label}</span>
    </div>
  );
}

/* ---------- Floating particle definitions ---------- */
const PARTICLES = [
  { left: "3%", dur: "22s", delay: "0s", size: 0 },
  { left: "7%", dur: "24s", delay: "3s", size: 1 },
  { left: "12%", dur: "19s", delay: "7s", size: 2 },
  { left: "17%", dur: "26s", delay: "1s", size: 0 },
  { left: "22%", dur: "28s", delay: "5s", size: 1 },
  { left: "27%", dur: "21s", delay: "9s", size: 2 },
  { left: "32%", dur: "23s", delay: "2s", size: 0 },
  { left: "37%", dur: "25s", delay: "6s", size: 1 },
  { left: "42%", dur: "20s", delay: "11s", size: 2 },
  { left: "47%", dur: "27s", delay: "0s", size: 0 },
  { left: "52%", dur: "22s", delay: "4s", size: 1 },
  { left: "57%", dur: "18s", delay: "8s", size: 2 },
  { left: "62%", dur: "24s", delay: "1s", size: 0 },
  { left: "67%", dur: "26s", delay: "6s", size: 1 },
  { left: "72%", dur: "21s", delay: "10s", size: 2 },
  { left: "77%", dur: "23s", delay: "3s", size: 0 },
  { left: "82%", dur: "19s", delay: "7s", size: 1 },
  { left: "87%", dur: "25s", delay: "2s", size: 2 },
  { left: "92%", dur: "20s", delay: "5s", size: 0 },
  { left: "97%", dur: "28s", delay: "9s", size: 1 },
  { left: "10%", dur: "30s", delay: "12s", size: 0 },
  { left: "35%", dur: "17s", delay: "4s", size: 2 },
  { left: "55%", dur: "29s", delay: "8s", size: 1 },
  { left: "75%", dur: "16s", delay: "0s", size: 2 },
];

/* ---------- Home page ---------- */
function Home() {
  const containerRef = useRef(null);
  const progress = useTreeScroll(containerRef);
  const [heroVisible, setHeroVisible] = useState(false);

  // Framer-motion scroll-linked parallax (GPU-accelerated via ScrollTimeline)
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start start", "end end"] });

  // Spring-smoothed scroll — buttery elastic parallax instead of snapping
  const springConfig = { stiffness: 80, damping: 20, restDelta: 0.001 };
  const smoothProgress = useSpring(scrollYProgress, springConfig);

  // Scroll progress bar value (uses smooth spring)
  const progressBarScaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  // Scroll velocity — subtle skew on the vine based on scroll speed
  const scrollVelocity = useVelocity(scrollYProgress);
  const velocitySkew = useSpring(
    useTransform(scrollVelocity, [-0.5, 0, 0.5], [-2, 0, 2]),
    { stiffness: 150, damping: 15 }
  );

  const orbY1  = useTransform(smoothProgress, [0, 1], [0, -200]);
  const orbY2  = useTransform(smoothProgress, [0, 1], [0, -100]);
  const orbY3  = useTransform(smoothProgress, [0, 1], [0, -280]);
  const orbY4  = useTransform(smoothProgress, [0, 1], [0, -60]);
  const orbY5  = useTransform(smoothProgress, [0, 1], [0, -150]);
  const orbY6  = useTransform(smoothProgress, [0, 1], [0, -220]);
  const orbY7  = useTransform(smoothProgress, [0, 1], [0, -90]);
  const orbY8  = useTransform(smoothProgress, [0, 1], [0, -170]);
  const orbY9  = useTransform(smoothProgress, [0, 1], [0, -130]);
  const orbY10 = useTransform(smoothProgress, [0, 1], [0, -240]);
  const orbY11 = useTransform(smoothProgress, [0, 1], [0, -50]);
  const orbY12 = useTransform(smoothProgress, [0, 1], [0, -180]);
  const orbTransforms = [orbY1, orbY2, orbY3, orbY4, orbY5, orbY6, orbY7, orbY8, orbY9, orbY10, orbY11, orbY12];

  // About Me photo: subtle parallax float + scale (spring-smoothed)
  const photoY = useTransform(smoothProgress, [0.7, 1], [30, 0]);
  const photoScale = useTransform(smoothProgress, [0.7, 0.9], [0.92, 1]);

  useEffect(() => {
    const timer = setTimeout(() => setHeroVisible(true), 200);
    return () => clearTimeout(timer);
  }, []);

  // Hero fades out before first card appears at p≈0.22
  const heroOpacity = Math.max(0, 1 - progress * 5.5);

  return (
    <div
      className="home-tree-container"
      ref={containerRef}
      style={{
        background: `linear-gradient(180deg, #FAFAF8 0%, rgba(32,178,170,${(0.005 + progress * 0.005).toFixed(4)}) 100%)`,
      }}
    >
      {/* Scroll progress bar — thin teal indicator at top */}
      <motion.div className="home-scroll-progress" style={{ scaleX: progressBarScaleX }} />

      {/* Parallax depth orbs — GPU-accelerated via framer-motion useTransform */}
      <div className="home-depth-orbs">
        {orbTransforms.map((y, i) => (
          <motion.div key={i} className={`home-depth-orb home-depth-orb-${i + 1}`} style={{ y }} />
        ))}
      </div>

      {/* Floating ambient particles */}
      <div className="home-particles">
        {PARTICLES.map((p, i) => (
          <span
            key={i}
            className={`home-particle home-particle-${p.size}`}
            style={{ left: p.left, animationDuration: p.dur, animationDelay: p.delay }}
          />
        ))}
      </div>

      {/* Full-page vine that extends behind footer/bottom sections — skews with scroll velocity */}
      <motion.div className="home-vine-backdrop" style={{ skewY: velocitySkew }}>
        <TreeVine progress={progress} sections={SECTIONS} />
      </motion.div>

      {/* Sticky viewport with overlaid cards */}
      <div className="home-tree-canvas-wrap">

        {/* Hero text — fades out on scroll */}
        <motion.div
          className={`home-tree-hero ${heroVisible ? "visible" : ""}`}
          style={{ opacity: heroOpacity, pointerEvents: heroOpacity < 0.3 ? "none" : "auto" }}
        >
          <div className="home-tree-hero-name">
            <LetterReveal text="Eugen Dunca" className="home-hero-name-text" delay={300} />
          </div>
          <p className="home-tree-hero-tagline">
            Engineering intelligence for energy systems
          </p>
          <div className="home-scroll-indicator">
            <span>Scroll to explore</span>
            <div className="home-scroll-arrow" />
          </div>
        </motion.div>

        {/* Branch cards with enter/sit/exit */}
        {SECTIONS.map((section) => (
          <BranchCard key={section.label} section={section} progress={progress} />
        ))}

      </div>

      {/* Scroll spacer — this is what makes the page scrollable */}
      <div className="home-tree-spacer" />

      {/* About Me card — vine dims behind, card takes focus */}
      <div className="home-about-section">
          <div className="home-about-card home-about-card--has-photo">
            <motion.img
              src={profilePhoto}
              alt="Eugen Dunca"
              className="home-about-card-photo"
              style={{ y: photoY, scale: photoScale }}
            />
            <h2 className="home-about-card-title">About Me</h2>
            <p className="home-about-card-text">
              I'm Eugen Dunca — an energy systems engineer building SCADA infrastructure,
              AI-driven dispatch optimization, and hybrid virtual power plant platforms at Waldevar Energy.
              My work spans real-time monitoring with Ignition, time series data pipelines, and
              ML-based load forecasting — bridging the gap between energy infrastructure and intelligent software.
            </p>
            <p className="home-about-card-subtext">
              This portfolio is a window into my work across energy, robotics, and AI — built for those
              who value where technology meets real-world impact.
            </p>
            <Link to="/experience" className="home-about-card-btn">
              View My Experience →
            </Link>
          </div>
      </div>

      {/* Bottom section — always visible, self-contained animations */}
      <div className="home-bottom-section">
        <motion.blockquote
          className="home-quote-new"
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <p>"The impediment to action advances action. What stands in the way becomes the way."</p>
          <cite>— Marcus Aurelius</cite>
        </motion.blockquote>

        <div className="home-bottom-stats">
          <StatCounter label="Energy Systems" end={5} />
          <StatCounter label="Optimization Models" end={3} />
          <StatCounter label={<>Years Building<br />AI/ML Models</>} end={5} />
        </div>

        <div className="home-bottom-cta">
          <p className="home-bottom-cta-text">
            Interested in my energy systems work? Explore the details or reach out.
          </p>
          <div className="home-bottom-cta-links">
            <Link to="/experience" className="home-bottom-cta-btn home-bottom-cta-btn--primary">
              View More →
            </Link>
            <Link to="/about" className="home-bottom-cta-btn home-bottom-cta-btn--secondary">
              About Me
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
